import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

export type StudioAppClass = 'static' | 'dynamic' | 'hybrid'
export type StudioProjectOutputKind = 'app' | 'document' | 'game'
export type StudioRuntimeTarget = 'web' | 'rn' | 'flutter' | 'mobile' | 'desktop'
export type StudioSecurityProfile = 'standard' | 'enterprise' | 'financial'
export type LegacyAppTarget = 'web' | 'mobile' | 'both'
export type StudioPlanningSource = 'cogo-decompose-api' | 'local-planner'
export type StudioAdaptiveSituationSignal =
  | 'device-runtime'
  | 'user-context'
  | 'business-state'
  | 'network'
  | 'policy'
  | 'evidence-quality'
export type StudioAdaptiveAxis =
  | 'layout-density'
  | 'workflow'
  | 'action-availability'
  | 'data-scope'
  | 'renderer-target'
  | 'design-style'
  | 'security-policy'

export interface StudioPerformanceBudget {
  firstPreviewP95Ms?: number
  warmRetrievalP95Ms?: number
  evidenceCompleteP95Ms?: number
}

export interface StudioAdaptiveRuntimePlan {
  schemaVersion: 'studio-adaptive-runtime-plan.v1'
  enabled: boolean
  situationSignals: StudioAdaptiveSituationSignal[]
  adaptationAxes: StudioAdaptiveAxis[]
  localStatePolicy: 'block-local-first'
  globalStatePolicy: 'appData-shared'
  evidencePolicy: {
    requireMeasuredDesignEvidence: boolean
    quarantineUnmeasuredOntologyFacts: boolean
    requireActionFlowTraceForDynamicApps: boolean
  }
  notes: string[]
}

export interface StudioDecomposePlanningMetadata {
  schemaVersion: 'studio-decompose-planning.v1'
  planningSource: StudioPlanningSource
  status: 'live' | 'fallback'
  plannerMode: 'proxy' | 'direct' | 'local'
  generatedAt: string
  requestUrl?: string
  authMs?: number
  upstreamMs?: number
  totalMs?: number
  elapsedMs?: number
  model?: string
  provider?: string
  decomposeWorkerVersion?: string
  attemptCount?: number
  repairAttempted?: boolean
  proxyContract?: 'appSpec' | 'missing-appSpec' | 'non-json'
  fallbackReason?: string
}

export interface StudioProjectPlan {
  intent: string
  outputKind?: StudioProjectOutputKind
  appClass: StudioAppClass
  runtimeTargets: StudioRuntimeTarget[]
  /** Canonical semantic design-system identity used by generation. */
  designSystemId?: string
  /** Mutable database preset row bound to the project canvas. */
  designSystemPresetId?: string
  /** Mutable personal style preset. Never ontology release authority. */
  stylePresetId?: string
  /** Exact immutable release binding carried into generation and publication. */
  designSystemRelease?: StudioDesignSystemReleaseIdentity
  /** Deterministic receipt proving Library semantics were bound to Design Ontology. */
  designOntologyBinding?: StudioDesignOntologyBindingEvidence
  templateId?: string
  dynamicIslands?: string[]
  securityProfile?: StudioSecurityProfile
  performanceBudget?: StudioPerformanceBudget
  adaptiveRuntime?: StudioAdaptiveRuntimePlan
}

export interface StudioDesignSystemReleaseIdentity {
  schemaVersion: 'cogo.studio-design-system-release-binding.v1'
  packageId: string
  packageVersion: string
  releaseId: string
  packageSnapshotHash: string
  identityHash: string
  catalogContentHash: string
  immutable: true
  activationScope: 'project'
  targetPlatforms: string[]
}

export interface StudioDesignOntologyBindingEvidence {
  schemaVersion: 'cogo.studio-design-ontology-binding-evidence.v1'
  designOntologyReleaseRef: string
  applicationSemanticHash: string
  designBindingHash: string
  renderContractHash: string
}

export const STUDIO_PROJECT_PLAN_SETTINGS_KEY = 'studioProjectPlan'
export const STUDIO_PLANNING_METADATA_SETTINGS_KEY = 'studioPlanningMetadata'

/**
 * F9 — these are human-facing labels, not raw enum values, and they used to be
 * one English map composed into `Dynamic · Web` for every locale, visible in
 * the BottomBar as text AND as its `title`. The maps are keyed by locale now;
 * the enum keys themselves are unchanged.
 */
const APP_CLASS_LABELS: Record<LocaleCode, Record<StudioAppClass, string>> = {
  en: { static: 'Static', dynamic: 'Dynamic', hybrid: 'Hybrid' },
  ko: { static: '정적', dynamic: '동적', hybrid: '혼합' },
  ja: { static: '静的', dynamic: '動的', hybrid: 'ハイブリッド' },
  zh: { static: '静态', dynamic: '动态', hybrid: '混合' },
  'zh-TW': { static: '靜態', dynamic: '動態', hybrid: '混合' },
}

const SECURITY_LABELS: Record<StudioSecurityProfile, string> = {
  standard: 'Standard',
  enterprise: 'Enterprise',
  financial: 'Financial',
}

const RUNTIME_LABELS: Record<LocaleCode, Record<StudioRuntimeTarget, string>> = {
  // Product names (React Native, Flutter) are proper nouns and stay as they are.
  en: { web: 'Web', rn: 'React Native', flutter: 'Flutter', mobile: 'Mobile', desktop: 'Desktop' },
  ko: { web: '웹', rn: 'React Native', flutter: 'Flutter', mobile: '모바일', desktop: '데스크톱' },
  ja: { web: 'ウェブ', rn: 'React Native', flutter: 'Flutter', mobile: 'モバイル', desktop: 'デスクトップ' },
  zh: { web: '网页', rn: 'React Native', flutter: 'Flutter', mobile: '移动端', desktop: '桌面端' },
  'zh-TW': { web: '網頁', rn: 'React Native', flutter: 'Flutter', mobile: '行動裝置', desktop: '桌面' },
}

const VALID_APP_CLASSES = new Set<StudioAppClass>(['static', 'dynamic', 'hybrid'])
const VALID_OUTPUT_KINDS = new Set<StudioProjectOutputKind>(['app', 'document', 'game'])
const VALID_RUNTIME_TARGETS = new Set<StudioRuntimeTarget>(['web', 'rn', 'flutter', 'mobile', 'desktop'])
const VALID_SECURITY_PROFILES = new Set<StudioSecurityProfile>(['standard', 'enterprise', 'financial'])
const VALID_ADAPTIVE_SIGNALS = new Set<StudioAdaptiveSituationSignal>([
  'device-runtime',
  'user-context',
  'business-state',
  'network',
  'policy',
  'evidence-quality',
])
const VALID_ADAPTIVE_AXES = new Set<StudioAdaptiveAxis>([
  'layout-density',
  'workflow',
  'action-availability',
  'data-scope',
  'renderer-target',
  'design-style',
  'security-policy',
])

const DEFAULT_PERFORMANCE_BUDGET: StudioPerformanceBudget = {
  firstPreviewP95Ms: 3000,
  warmRetrievalP95Ms: 800,
  evidenceCompleteP95Ms: 10000,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function isStudioAppClass(value: unknown): value is StudioAppClass {
  return typeof value === 'string' && VALID_APP_CLASSES.has(value as StudioAppClass)
}

function isStudioProjectOutputKind(value: unknown): value is StudioProjectOutputKind {
  return typeof value === 'string' && VALID_OUTPUT_KINDS.has(value as StudioProjectOutputKind)
}

export function inferStudioProjectOutputKind(input: string): StudioProjectOutputKind {
  const text = input.toLowerCase()
  if (/\b(game|gameplay|puzzle|arcade|rpg|platformer|visual novel)\b|게임|퍼즐|ゲーム|游戏|遊戲/.test(text)) {
    return 'game'
  }
  if (
    /\b(document|report|magazine|book|whitepaper|article|proposal|presentation|slides?|spreadsheet|docx|hwpx|pptx|pdf)\b|문서|보고서|매거진|책|제안서|프레젠테이션|ドキュメント|レポート|文書|报告|報告/.test(text)
  ) {
    return 'document'
  }
  return 'app'
}

function isRuntimeTarget(value: unknown): value is StudioRuntimeTarget {
  return typeof value === 'string' && VALID_RUNTIME_TARGETS.has(value as StudioRuntimeTarget)
}

function isSecurityProfile(value: unknown): value is StudioSecurityProfile {
  return typeof value === 'string' && VALID_SECURITY_PROFILES.has(value as StudioSecurityProfile)
}

function isAdaptiveSignal(value: unknown): value is StudioAdaptiveSituationSignal {
  return typeof value === 'string' && VALID_ADAPTIVE_SIGNALS.has(value as StudioAdaptiveSituationSignal)
}

function isAdaptiveAxis(value: unknown): value is StudioAdaptiveAxis {
  return typeof value === 'string' && VALID_ADAPTIVE_AXES.has(value as StudioAdaptiveAxis)
}

function normalizeBudget(value: unknown): StudioPerformanceBudget | undefined {
  if (!isRecord(value)) return undefined
  const budget: StudioPerformanceBudget = {}
  for (const key of ['firstPreviewP95Ms', 'warmRetrievalP95Ms', 'evidenceCompleteP95Ms'] as const) {
    const raw = value[key]
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      budget[key] = raw
    }
  }
  return Object.keys(budget).length > 0 ? budget : undefined
}

function unique<T extends string>(items: T[]): T[] {
  return [...new Set(items)]
}

const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/
const DESIGN_ONTOLOGY_HASH_PATTERN = /^[a-f0-9]{64}$/

export function normalizeStudioDesignSystemRelease(value: unknown): StudioDesignSystemReleaseIdentity | undefined {
  if (!isRecord(value)) return undefined
  const targetPlatforms = Array.isArray(value.targetPlatforms)
    ? value.targetPlatforms.filter((target): target is string => typeof target === 'string' && target.trim().length > 0)
    : []
  if (
    value.schemaVersion !== 'cogo.studio-design-system-release-binding.v1' ||
    typeof value.packageId !== 'string' ||
    !value.packageId.trim() ||
    typeof value.packageVersion !== 'string' ||
    !value.packageVersion.trim() ||
    typeof value.releaseId !== 'string' ||
    !value.releaseId.trim() ||
    typeof value.packageSnapshotHash !== 'string' ||
    !CONTENT_HASH_PATTERN.test(value.packageSnapshotHash) ||
    typeof value.identityHash !== 'string' ||
    !CONTENT_HASH_PATTERN.test(value.identityHash) ||
    typeof value.catalogContentHash !== 'string' ||
    !CONTENT_HASH_PATTERN.test(value.catalogContentHash) ||
    value.immutable !== true ||
    value.activationScope !== 'project' ||
    targetPlatforms.length === 0
  ) {
    return undefined
  }
  return {
    schemaVersion: 'cogo.studio-design-system-release-binding.v1',
    packageId: value.packageId.trim(),
    packageVersion: value.packageVersion.trim(),
    releaseId: value.releaseId.trim(),
    packageSnapshotHash: value.packageSnapshotHash,
    identityHash: value.identityHash,
    catalogContentHash: value.catalogContentHash,
    immutable: true,
    activationScope: 'project',
    targetPlatforms: unique(targetPlatforms),
  }
}

export function normalizeStudioDesignOntologyBinding(
  value: unknown,
): StudioDesignOntologyBindingEvidence | undefined {
  if (!isRecord(value)) return undefined
  if (
    value.schemaVersion !==
      'cogo.studio-design-ontology-binding-evidence.v1' ||
    typeof value.designOntologyReleaseRef !== 'string' ||
    !value.designOntologyReleaseRef.trim() ||
    typeof value.applicationSemanticHash !== 'string' ||
    !DESIGN_ONTOLOGY_HASH_PATTERN.test(value.applicationSemanticHash) ||
    typeof value.designBindingHash !== 'string' ||
    !DESIGN_ONTOLOGY_HASH_PATTERN.test(value.designBindingHash) ||
    typeof value.renderContractHash !== 'string' ||
    !DESIGN_ONTOLOGY_HASH_PATTERN.test(value.renderContractHash)
  ) {
    return undefined
  }
  return {
    schemaVersion: 'cogo.studio-design-ontology-binding-evidence.v1',
    designOntologyReleaseRef: value.designOntologyReleaseRef.trim(),
    applicationSemanticHash: value.applicationSemanticHash,
    designBindingHash: value.designBindingHash,
    renderContractHash: value.renderContractHash,
  }
}

export function preservesSelectedDesignSystemRelease(
  selectedPlan: StudioProjectPlan,
  returnedPlan: StudioProjectPlan | null | undefined,
): boolean {
  const selected = selectedPlan.designSystemRelease
  if (!selected) return true
  const returned = returnedPlan?.designSystemRelease
  return Boolean(
    returned &&
    returned.schemaVersion === selected.schemaVersion &&
    returned.packageId === selected.packageId &&
    returned.packageVersion === selected.packageVersion &&
    returned.releaseId === selected.releaseId &&
    returned.packageSnapshotHash === selected.packageSnapshotHash &&
    returned.identityHash === selected.identityHash &&
    returned.catalogContentHash === selected.catalogContentHash &&
    returned.immutable === true &&
    returned.activationScope === 'project' &&
    returned.targetPlatforms.length === selected.targetPlatforms.length &&
    returned.targetPlatforms.every((target) => selected.targetPlatforms.includes(target)),
  )
}

function buildAdaptiveRuntimePlan(input: {
  appClass: StudioAppClass
  runtimeTargets: StudioRuntimeTarget[]
  securityProfile: StudioSecurityProfile
  prompt?: string
}): StudioAdaptiveRuntimePlan {
  const lower = input.prompt?.toLowerCase() ?? ''
  const nativeRequested = input.runtimeTargets.some((target) => (
    target === 'mobile' || target === 'rn' || target === 'flutter'
  ))
  const dynamic = input.appClass === 'dynamic' || input.appClass === 'hybrid'
  const regulated = input.securityProfile === 'enterprise' || input.securityProfile === 'financial'
  const situationSignals = unique<StudioAdaptiveSituationSignal>([
    'device-runtime',
    'evidence-quality',
    ...(dynamic ? ['user-context', 'business-state'] as const : []),
    ...(nativeRequested || /\b(offline|slow network|mobile|field)\b/.test(lower) ? ['network'] as const : []),
    ...(regulated ? ['policy'] as const : []),
  ])
  const adaptationAxes = unique<StudioAdaptiveAxis>([
    'layout-density',
    'renderer-target',
    'design-style',
    ...(dynamic ? ['workflow', 'action-availability', 'data-scope'] as const : []),
    ...(regulated ? ['security-policy'] as const : []),
  ])

  return {
    schemaVersion: 'studio-adaptive-runtime-plan.v1',
    enabled: true,
    situationSignals,
    adaptationAxes,
    localStatePolicy: 'block-local-first',
    globalStatePolicy: 'appData-shared',
    evidencePolicy: {
      requireMeasuredDesignEvidence: true,
      quarantineUnmeasuredOntologyFacts: true,
      requireActionFlowTraceForDynamicApps: dynamic,
    },
    notes: [
      'Adaptation must preserve block-local state boundaries before writing shared appData.',
      'Ontology promotion remains blocked until rendered design evidence is measured.',
      ...(dynamic ? ['Dynamic apps must prove ActionFlow traces before adaptive facts are promoted.'] : []),
      ...(regulated ? ['Enterprise and financial apps must keep policy/security gates ahead of publish readiness.'] : []),
    ],
  }
}

function normalizeAdaptiveRuntimePlan(value: unknown): StudioAdaptiveRuntimePlan | undefined {
  const raw = isRecord(value) ? value : null
  if (!raw || raw.schemaVersion !== 'studio-adaptive-runtime-plan.v1') return undefined
  const situationSignals = Array.isArray(raw.situationSignals)
    ? raw.situationSignals.filter(isAdaptiveSignal)
    : []
  const adaptationAxes = Array.isArray(raw.adaptationAxes)
    ? raw.adaptationAxes.filter(isAdaptiveAxis)
    : []
  const evidencePolicy = isRecord(raw.evidencePolicy) ? raw.evidencePolicy : {}
  return {
    schemaVersion: 'studio-adaptive-runtime-plan.v1',
    enabled: raw.enabled !== false,
    situationSignals,
    adaptationAxes,
    localStatePolicy: 'block-local-first',
    globalStatePolicy: 'appData-shared',
    evidencePolicy: {
      requireMeasuredDesignEvidence: evidencePolicy.requireMeasuredDesignEvidence !== false,
      quarantineUnmeasuredOntologyFacts: evidencePolicy.quarantineUnmeasuredOntologyFacts !== false,
      requireActionFlowTraceForDynamicApps: evidencePolicy.requireActionFlowTraceForDynamicApps !== false,
    },
    notes: Array.isArray(raw.notes)
      ? raw.notes.filter((item): item is string => typeof item === 'string')
      : [],
  }
}

export function runtimeTargetsForAppTarget(appTarget: string | null | undefined): StudioRuntimeTarget[] {
  if (appTarget === 'mobile') return ['mobile']
  if (appTarget === 'both') return ['web', 'mobile']
  return ['web']
}

export function appTargetFromRuntimeTargets(targets: StudioRuntimeTarget[]): LegacyAppTarget {
  const hasWeb = targets.includes('web') || targets.includes('desktop')
  const hasMobile = targets.includes('mobile') || targets.includes('rn') || targets.includes('flutter')
  if (hasWeb && hasMobile) return 'both'
  if (hasMobile) return 'mobile'
  return 'web'
}

export function createStudioProjectPlan(input: {
  intent?: string
  name: string
  outputKind?: StudioProjectOutputKind
  appClass: StudioAppClass
  runtimeTargets: StudioRuntimeTarget[]
  designSystemId?: string | null
  designSystemPresetId?: string | null
  stylePresetId?: string | null
  designSystemRelease?: StudioDesignSystemReleaseIdentity | null
  designOntologyBinding?: StudioDesignOntologyBindingEvidence | null
  templateId?: string | null
  securityProfile?: StudioSecurityProfile
  adaptiveRuntime?: StudioAdaptiveRuntimePlan | null
}): StudioProjectPlan {
  const runtimeTargets = input.runtimeTargets.filter(isRuntimeTarget)
  const appClass = isStudioAppClass(input.appClass) ? input.appClass : 'static'
  const intent = input.intent?.trim() || input.name.trim() || 'Untitled app'
  const securityProfile = input.securityProfile ?? 'standard'
  const designSystemRelease = normalizeStudioDesignSystemRelease(input.designSystemRelease)
  const designOntologyBinding = normalizeStudioDesignOntologyBinding(
    input.designOntologyBinding,
  )

  return {
    intent,
    outputKind: input.outputKind ?? inferStudioProjectOutputKind(`${input.name} ${intent}`),
    appClass,
    runtimeTargets: runtimeTargets.length > 0 ? runtimeTargets : ['web'],
    ...(designSystemRelease
      ? {
          designSystemId: designSystemRelease.packageId,
          designSystemRelease,
        }
      : input.designSystemId
        ? { designSystemId: input.designSystemId }
        : {}),
    ...(input.designSystemPresetId
      ? { designSystemPresetId: input.designSystemPresetId }
      : {}),
    ...(!designSystemRelease && input.stylePresetId
      ? { stylePresetId: input.stylePresetId }
      : {}),
    ...(designOntologyBinding ? { designOntologyBinding } : {}),
    ...(input.templateId ? { templateId: input.templateId } : {}),
    ...(appClass === 'static' ? {} : { dynamicIslands: [] }),
    securityProfile,
    performanceBudget: DEFAULT_PERFORMANCE_BUDGET,
    adaptiveRuntime: input.adaptiveRuntime ?? buildAdaptiveRuntimePlan({
      appClass,
      runtimeTargets: runtimeTargets.length > 0 ? runtimeTargets : ['web'],
      securityProfile,
      prompt: intent,
    }),
  }
}

export function runtimeTargetsForPrompt(prompt: string): StudioRuntimeTarget[] {
  const lower = prompt.toLowerCase()
  const positiveText = stripNegatedPromptClauses(lower)
  const targets = new Set<StudioRuntimeTarget>()

  if (/\b(web[-\s]?only|target web only|web target only)\b/.test(lower)) {
    return ['web']
  }

  if (/\b(web|website|browser)\b/.test(positiveText)) targets.add('web')
  if (/\b(react\s+native|rn)\b/.test(positiveText)) targets.add('rn')
  if (/\bflutter\b/.test(positiveText)) targets.add('flutter')
  if (/\b(desktop)\b/.test(positiveText)) targets.add('desktop')

  const mentionsMobile = /\b(mobile|ios|android)\b/.test(positiveText)
  if (mentionsMobile && !targets.has('rn') && !targets.has('flutter')) {
    targets.add('mobile')
  }

  return targets.size > 0 ? [...targets] : ['web']
}

function stripNegatedPromptClauses(input: string): string {
  return input
    .replace(/\b(?:no|without)\s+[^,.;]+/g, ' ')
    .replace(/\bdo not\s+(?:add|build|create|generate|include|make|target|use)\s+[^,.;]+/g, ' ')
    .replace(/\bdon't\s+(?:add|build|create|generate|include|make|target|use)\s+[^,.;]+/g, ' ')
}

function hasCollectionLikeValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  if (!isRecord(value)) return false
  const sources = value.sources
  if (Array.isArray(sources)) return sources.length > 0
  if (isRecord(sources)) return Object.keys(sources).length > 0
  return Object.keys(value).length > 0
}

function inferGeneratedAppClass(input: {
  prompt?: string
  description?: string | null
  interactionContract?: { actionFlowRequired?: boolean } | null
  datasources?: unknown
  globalState?: unknown
}): StudioAppClass {
  const text = `${input.prompt ?? ''} ${input.description ?? ''}`.toLowerCase()
  if (/\b(hybrid|dynamic island|island)\b/.test(text)) return 'hybrid'
  if (/\b(static|landing|portfolio|brochure|marketing page)\b/.test(text) && !input.interactionContract?.actionFlowRequired) {
    return 'static'
  }
  if (
    input.interactionContract?.actionFlowRequired ||
    hasCollectionLikeValue(input.datasources) ||
    hasCollectionLikeValue(input.globalState) ||
    /\b(dynamic|dashboard|workflow|form|order|checkout|task|todo|crm|analytics|datasource|database)\b/.test(text)
  ) {
    return 'dynamic'
  }
  return 'static'
}

function inferSecurityProfileFromPrompt(prompt: string): StudioSecurityProfile {
  const lower = prompt.toLowerCase()
  const explicitProfile = /\bsecurity\s+profile\s*:\s*(standard|enterprise|financial)\b/.exec(lower)?.[1]
  if (explicitProfile === 'standard' || explicitProfile === 'enterprise' || explicitProfile === 'financial') {
    return explicitProfile
  }
  if (/\b(financial|finance|trading|bank|banking|payment|payments|wallet|crypto)\b/.test(lower)) {
    return 'financial'
  }
  if (/\b(enterprise|private|regulated|compliance|internal|secure|security)\b/.test(lower)) {
    return 'enterprise'
  }
  return 'standard'
}

function runtimeTargetsForGeneration(input: {
  prompt: string
  runtimeTargets?: readonly string[] | null
  targetPlatform?: string | null
  generationTargetPlatform?: string | null
}): StudioRuntimeTarget[] {
  const explicitTargets = (input.runtimeTargets ?? []).filter(isRuntimeTarget)
  if (explicitTargets.length > 0) return explicitTargets
  if (input.generationTargetPlatform === 'flutter') return ['flutter']
  if (input.targetPlatform === 'mobile') return ['mobile']
  if (input.targetPlatform === 'both') return ['web', 'mobile']
  return runtimeTargetsForPrompt(input.prompt)
}

export function deriveStudioProjectPlanFromGeneration(input: {
  prompt: string
  name?: string | null
  appClass?: string | null
  description?: string | null
  runtimeTargets?: readonly string[] | null
  targetPlatform?: string | null
  generationTargetPlatform?: string | null
  interactionContract?: { actionFlowRequired?: boolean } | null
  datasources?: unknown
  globalState?: unknown
  designSystemId?: string | null
  designSystemPresetId?: string | null
  stylePresetId?: string | null
  designSystemRelease?: StudioDesignSystemReleaseIdentity | null
  designOntologyBinding?: StudioDesignOntologyBindingEvidence | null
}): StudioProjectPlan {
  return createStudioProjectPlan({
    name: input.name ?? 'Untitled app',
    intent: input.description ?? input.prompt,
    appClass: isStudioAppClass(input.appClass) ? input.appClass : inferGeneratedAppClass(input),
    runtimeTargets: runtimeTargetsForGeneration(input),
    designSystemId: input.designSystemId ?? undefined,
    designSystemPresetId: input.designSystemPresetId ?? undefined,
    stylePresetId: input.stylePresetId ?? undefined,
    designSystemRelease: input.designSystemRelease ?? undefined,
    designOntologyBinding: input.designOntologyBinding ?? undefined,
    securityProfile: inferSecurityProfileFromPrompt(input.prompt),
  })
}

export function deriveStudioProjectPlanFromMeta(meta: {
  name?: string | null
  description?: string | null
  app_target?: string | null
  design_system_preset_id?: string | null
}): StudioProjectPlan {
  return createStudioProjectPlan({
    name: meta.name ?? 'Untitled app',
    intent: meta.description ?? meta.name ?? undefined,
    appClass: 'static',
    runtimeTargets: runtimeTargetsForAppTarget(meta.app_target),
    designSystemPresetId: meta.design_system_preset_id ?? undefined,
    stylePresetId: meta.design_system_preset_id ?? undefined,
    securityProfile: 'standard',
  })
}

export function readStudioProjectPlan(settings: unknown): StudioProjectPlan | null {
  if (!isRecord(settings)) return null
  const raw = settings[STUDIO_PROJECT_PLAN_SETTINGS_KEY] ?? settings.projectPlan
  if (!isRecord(raw)) return null

  const appClass = isStudioAppClass(raw.appClass) ? raw.appClass : 'static'
  const runtimeTargets = Array.isArray(raw.runtimeTargets)
    ? raw.runtimeTargets.filter(isRuntimeTarget)
    : []
  const intent = typeof raw.intent === 'string' && raw.intent.trim()
    ? raw.intent.trim()
    : 'Untitled app'

  const designOntologyBinding = normalizeStudioDesignOntologyBinding(
    raw.designOntologyBinding,
  )

  return {
    intent,
    outputKind: isStudioProjectOutputKind(raw.outputKind)
      ? raw.outputKind
      : inferStudioProjectOutputKind(intent),
    appClass,
    runtimeTargets: runtimeTargets.length > 0 ? runtimeTargets : ['web'],
    designSystemId: typeof raw.designSystemId === 'string' ? raw.designSystemId : undefined,
    designSystemPresetId:
      typeof raw.designSystemPresetId === 'string'
        ? raw.designSystemPresetId
        : undefined,
    stylePresetId: typeof raw.stylePresetId === 'string' ? raw.stylePresetId : undefined,
    designSystemRelease: normalizeStudioDesignSystemRelease(raw.designSystemRelease),
    ...(designOntologyBinding ? { designOntologyBinding } : {}),
    templateId: typeof raw.templateId === 'string' ? raw.templateId : undefined,
    dynamicIslands: Array.isArray(raw.dynamicIslands)
      ? raw.dynamicIslands.filter((item): item is string => typeof item === 'string')
      : undefined,
    securityProfile: isSecurityProfile(raw.securityProfile) ? raw.securityProfile : 'standard',
    performanceBudget: normalizeBudget(raw.performanceBudget),
    adaptiveRuntime: normalizeAdaptiveRuntimePlan(raw.adaptiveRuntime),
  }
}

export function withStudioProjectPlanSettings(
  existingSettings: unknown,
  plan: StudioProjectPlan | null | undefined,
): Record<string, unknown> {
  const settings = isRecord(existingSettings) ? { ...existingSettings } : {}
  if (plan) {
    const designOntologyBinding = normalizeStudioDesignOntologyBinding(
      plan.designOntologyBinding,
    )
    const {
      designOntologyBinding: _designOntologyBinding,
      ...planWithoutBinding
    } = plan
    settings[STUDIO_PROJECT_PLAN_SETTINGS_KEY] = {
      ...planWithoutBinding,
      ...(designOntologyBinding ? { designOntologyBinding } : {}),
    }
  }
  return settings
}

export function withStudioPlanningMetadataSettings(
  existingSettings: unknown,
  metadata: StudioDecomposePlanningMetadata | null | undefined,
): Record<string, unknown> {
  const settings = isRecord(existingSettings) ? { ...existingSettings } : {}
  if (!metadata) return settings
  const existingCogoMetadata = isRecord(settings.cogoApplicationMetadata)
    ? settings.cogoApplicationMetadata
    : {}
  settings[STUDIO_PLANNING_METADATA_SETTINGS_KEY] = metadata
  settings.cogoApplicationMetadata = {
    ...existingCogoMetadata,
    planningSource: metadata.planningSource,
    decomposePlanning: metadata,
  }
  return settings
}

export function formatStudioAppClass(
  value: StudioAppClass | undefined,
  locale: LocaleCode = getLocale(),
): string {
  const labels = APP_CLASS_LABELS[locale] ?? APP_CLASS_LABELS.en
  return labels[value ?? 'static']
}

export function formatSecurityProfile(value: StudioSecurityProfile | undefined): string {
  return value ? SECURITY_LABELS[value] : 'Standard'
}

export function formatRuntimeTargets(
  targets: StudioRuntimeTarget[] | undefined,
  locale: LocaleCode = getLocale(),
): string {
  const safeTargets: StudioRuntimeTarget[] = (targets ?? ['web']).filter(isRuntimeTarget)
  const displayTargets: StudioRuntimeTarget[] = safeTargets.length > 0 ? safeTargets : ['web']
  const labels = RUNTIME_LABELS[locale] ?? RUNTIME_LABELS.en
  return displayTargets.map((target) => labels[target]).join(' + ')
}

export function formatStudioProjectPlanSummary(
  plan: StudioProjectPlan | null | undefined,
  fallbackAppTarget?: string | null,
  locale: LocaleCode = getLocale(),
): string {
  const resolved = plan ?? createStudioProjectPlan({
    name: 'Untitled app',
    appClass: 'static',
    runtimeTargets: runtimeTargetsForAppTarget(fallbackAppTarget),
    securityProfile: 'standard',
  })
  return `${formatStudioAppClass(resolved.appClass, locale)} · ${formatRuntimeTargets(resolved.runtimeTargets, locale)}`
}
